import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, FormControl, InputLabel, Select, MenuItem, Typography, Paper
} from '@mui/material';
import { colors } from '@mui/material';
import { Fastfood, LocalDrink, Cake, Coffee, RestaurantMenu, LocalBar, EmojiFoodBeverage, Icecream, LocalPizza, LocalDining, RamenDining, SetMeal, LunchDining, Restaurant, Liquor, SoupKitchen, BreakfastDining, BakeryDining, DinnerDining, EggAlt, KebabDining, SportsBar } from '@mui/icons-material';

// Icon and color options
const categoryIcons = [
  { name: 'Fastfood', icon: <Fastfood /> },
  { name: 'Drinks', icon: <LocalDrink /> },
  { name: 'Dessert', icon: <Cake /> },
  { name: 'Coffee', icon: <Coffee /> },
  { name: 'Menu', icon: <RestaurantMenu /> },
  { name: 'Bar', icon: <LocalBar /> },
  { name: 'Tea', icon: <EmojiFoodBeverage /> },
  { name: 'Ice Cream', icon: <Icecream /> },
  { name: 'Pizza', icon: <LocalPizza /> },
  { name: 'Dining', icon: <LocalDining /> },
  { name: 'Ramen', icon: <RamenDining /> },
  { name: 'Sushi', icon: <SetMeal /> },
  { name: 'Lunch', icon: <LunchDining /> },
  { name: 'Restaurant', icon: <Restaurant /> },
  { name: 'Wine', icon: <Liquor /> },
  { name: 'Soup', icon: <SoupKitchen /> },
  { name: 'Breakfast', icon: <BreakfastDining /> },
  { name: 'Bakery', icon: <BakeryDining /> },
  { name: 'Dinner', icon: <DinnerDining /> },
  { name: 'Egg', icon: <EggAlt /> },
  { name: 'Kebab', icon: <KebabDining /> },
  { name: 'Tapas', icon: <SportsBar /> }
];

const categoryColors = [
  { name: 'Red', value: colors.red[500] },
  { name: 'Pink', value: colors.pink[500] },
  { name: 'Purple', value: colors.purple[500] },
  { name: 'Deep Purple', value: colors.deepPurple[500] },
  { name: 'Indigo', value: colors.indigo[500] },
  { name: 'Blue', value: colors.blue[500] },
  { name: 'Light Blue', value: colors.lightBlue[500] },
  { name: 'Cyan', value: colors.cyan[500] },
  { name: 'Teal', value: colors.teal[500] },
  { name: 'Green', value: colors.green[500] },
  { name: 'Light Green', value: colors.lightGreen[500] },
  { name: 'Lime', value: colors.lime[500] },
  { name: 'Yellow', value: colors.yellow[600] },
  { name: 'Amber', value: colors.amber[500] },
  { name: 'Orange', value: colors.orange[500] },
  { name: 'Deep Orange', value: colors.deepOrange[500] },
  { name: 'Brown', value: colors.brown[500] },
  { name: 'Grey', value: colors.grey[500] },
  { name: 'Blue Grey', value: colors.blueGrey[500] }
];

const AddCategoryDialog = ({ open, onClose, onAdd }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [iconName, setIconName] = useState('');
  const [colorName, setColorName] = useState('Blue');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  // Get color value from color name
  const colorValue = categoryColors.find(c => c.name === colorName)?.value || colors.blue[500];
  const selectedIcon = categoryIcons.find(i => i.name === iconName)?.icon;

  const handleAdd = () => {
    if (!name.trim()) {
      setError(t('category.nameRequired'));
      return;
    }
    onAdd({
      name,
      icon: iconName,
      colorName,
      colorValue,
      description
    });
    setName('');
    setIconName('');
    setColorName('Blue');
    setDescription('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setName('');
    setIconName('');
    setColorName('Blue');
    setDescription('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400, height: 500 }
      }}
    >
      <DialogTitle>{t('category.addTitle')}</DialogTitle>
      <DialogContent>
      <Box sx={{ height: 13 }} />
        <TextField
          label={t('category.name')}
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          error={!!error}
          helperText={error}
          autoFocus
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('category.selectIcon')}</InputLabel>
          <Select
            value={iconName}
            label={t('category.selectIcon')}
            onChange={e => setIconName(e.target.value)}
            renderValue={val =>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {categoryIcons.find(i => i.name === val)?.icon}
                <span>{val}</span>
              </Box>
            }
          >
            {categoryIcons.map(icon => (
              <MenuItem key={icon.name} value={icon.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {icon.icon}
                  <span>{icon.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('category.iconColor')}</InputLabel>
          <Select
            value={colorName}
            label={t('category.iconColor')}
            onChange={e => setColorName(e.target.value)}
            renderValue={val =>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: categoryColors.find(c => c.name === val)?.value, mr: 1 }} />
                <span>{val}</span>
              </Box>
            }
          >
            {categoryColors.map(color => (
              <MenuItem key={color.name} value={color.name}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: color.value, mr: 1 }} />
                  {color.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={t('category.description')}
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={3}
        />

        {/* Preview */}
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Paper
            elevation={3}
            sx={{
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              bgcolor: colorValue
            }}
          >
            {selectedIcon}
          </Paper>
          <Box>
            <Typography variant="subtitle2">{name || t('category.name')}</Typography>
            <Typography variant="caption" color="text.secondary">{description}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleAdd}>{t('common.add')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCategoryDialog;